"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InsightRenderer = void 0;
var tslib_1 = require("tslib");
// (C) 2020 GoodData Corporation
var react_1 = tslib_1.__importStar(require("react"));
var flatMap_1 = tslib_1.__importDefault(require("lodash/flatMap"));
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var merge_1 = tslib_1.__importDefault(require("lodash/merge"));
var sdk_model_1 = require("@gooddata/sdk-model");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var InsightRenderer_1 = require("../../../insightView/InsightRenderer");
var InsightError_1 = require("../../../insightView/InsightError");
var drillingUtils_1 = require("./drillingUtils");
var utils_1 = require("./utils");
var converters_1 = require("../../converters");
var contexts_1 = require("../../contexts");
var insightStyle = { width: "100%", height: "100%", position: "relative" };
exports.InsightRenderer = function (_a) {
    var _b, _c;
    var insightWidget = _a.insightWidget, insight = _a.insight, filters = _a.filters, filterContext = _a.filterContext, drillableItems = _a.drillableItems, onDrill = _a.onDrill, onError = _a.onError, backend = _a.backend, workspace = _a.workspace, ErrorComponent = _a.ErrorComponent, LoadingComponent = _a.LoadingComponent, clientHeight = _a.clientHeight;
    var effectiveBackend = sdk_ui_1.useBackend(backend);
    var effectiveWorkspace = sdk_ui_1.useWorkspace(workspace);
    var dashboardViewConfig = contexts_1.useDashboardViewConfig();
    var execConfig = contexts_1.useDashboardViewExecConfig();
    var userWorkspaceSettings = contexts_1.useUserWorkspaceSettings();
    var colorPalette = contexts_1.useColorPalette();
    var attributesWithDrillDown = contexts_1.useAttributesWithDrillDown();
    var _d = react_1.useState(false), isVisualizationLoading = _d[0], setIsVisualizationLoading = _d[1];
    var _e = react_1.useState([]), possibleDrills = _e[0], setPossibleDrills = _e[1];
    var _f = react_1.useState(null), visualizationError = _f[0], setVisualizationError = _f[1];
    var handleLoadingChanged = react_1.useCallback(function (_a) {
        var isLoading = _a.isLoading;
        setIsVisualizationLoading(isLoading);
        // if starting loading dismiss any previous visualizationError as it is most likely obsolete
        if (isLoading) {
            setVisualizationError(null);
        }
    }, []);
    var handleError = react_1.useCallback(function (error) {
        setVisualizationError(error);
        onError === null || onError === void 0 ? void 0 : onError(error);
    }, [onError]);
    var inputFilters = react_1.useMemo(function () {
        return filters
            ? converters_1.filterContextItemsToFiltersForWidget(filters, insightWidget)
            : converters_1.filterContextToFiltersForWidget(filterContext, insightWidget);
    }, [filters, filterContext, insightWidget]);
    var _g = sdk_ui_1.useCancelablePromise({
        promise: function () { return tslib_1.__awaiter(void 0, void 0, void 0, function () {
            var resolvedFilters, resolvedWithImplicitAllTime;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, effectiveBackend
                            .workspace(effectiveWorkspace)
                            .dashboards()
                            .getResolvedFiltersForWidget(insightWidget, inputFilters)];
                    case 1:
                        resolvedFilters = _a.sent();
                        resolvedWithImplicitAllTime = utils_1.addImplicitAllTimeFilter(insightWidget, resolvedFilters);
                        if (utils_1.isDateFilterIgnoredForInsight(insight)) {
                            resolvedWithImplicitAllTime = resolvedWithImplicitAllTime.filter(function (filter) { return !sdk_model_1.isDateFilter(filter); });
                        }
                        return [2 /*return*/, effectiveBackend
                                .workspace(effectiveWorkspace)
                                .insights()
                                .getInsightWithAddedFilters(insight, resolvedWithImplicitAllTime)];
                }
            });
        }); },
        onError: onError,
    }, [effectiveBackend, effectiveWorkspace, insightWidget, insight, inputFilters]), error = _g.error, insightWithFilters = _g.result, status = _g.status;
    var insightWithAddedWidgetProperties = react_1.useMemo(function () {
        var _a;
        if (!insightWithFilters) {
            return insightWithFilters;
        }
        var fromWidget = insightWidget.properties;
        if (!fromWidget) {
            return insightWithFilters;
        }
        var fromWidgetWithZoomingHandled = tslib_1.__assign(tslib_1.__assign({}, fromWidget), { controls: tslib_1.__assign(tslib_1.__assign({}, fromWidget === null || fromWidget === void 0 ? void 0 : fromWidget.controls), { 
                // we need to take the relevant feature flag into account as well
                zoomInsight: !!(userWorkspaceSettings.enableKDZooming && ((_a = fromWidget === null || fromWidget === void 0 ? void 0 : fromWidget.controls) === null || _a === void 0 ? void 0 : _a.zoomInsight)) }) });
        var fromInsight = sdk_model_1.insightProperties(insightWithFilters);
        var merged = merge_1.default({}, fromInsight, fromWidgetWithZoomingHandled);
        return sdk_model_1.insightSetProperties(insightWithFilters, merged);
    }, [insightWithFilters, insightWidget.properties, userWorkspaceSettings]);
    var implicitDrillDefinitions = react_1.useMemo(function () {
        return drillingUtils_1.getImplicitDrillsWithPredicates(insightWidget.drills, possibleDrills, attributesWithDrillDown);
    }, [insightWidget.drills, possibleDrills, attributesWithDrillDown]);
    var implicitDrills = react_1.useMemo(function () {
        return flatMap_1.default(implicitDrillDefinitions, function (info) { return info.predicates; });
    }, [implicitDrillDefinitions]);
    // since InsightRendererImpl only sets onDrill on the first render (this is a PlugVis API, there is no way to update onDrill there)
    // we have to sync the implicitDrillDefinitions into a ref so that the handleDrill can access the most recent value (thanks to the .current)
    // without this, handleDrill just closes over the first implicitDrillDefinitions which will never contain drillDown drills
    // as they are added *after* the first render of the PlugVis.
    var cachedImplicitDrillDefinitions = react_1.useRef(implicitDrillDefinitions);
    react_1.useEffect(function () {
        cachedImplicitDrillDefinitions.current = implicitDrillDefinitions;
    }, [implicitDrillDefinitions]);
    var handleDrill = react_1.useCallback(function (event) {
        var enrichedEvent = tslib_1.__assign(tslib_1.__assign({}, event), { widgetRef: insightWidget.ref });
        // if there are drillable items, we do not want to return any drillDefinitions as the implicit drills are not even used
        if (drillableItems) {
            return onDrill(enrichedEvent);
        }
        var facade = sdk_ui_1.DataViewFacade.for(event.dataView);
        var definitions = cachedImplicitDrillDefinitions.current;
        var matchingImplicitDrillDefinitions = definitions.filter(function (info) {
            return event.drillContext.intersection.some(function (intersection) {
                return sdk_ui_1.isSomeHeaderPredicateMatched(info.predicates, intersection.header, facade);
            });
        });
        return onDrill(tslib_1.__assign(tslib_1.__assign({}, enrichedEvent), { drillDefinitions: matchingImplicitDrillDefinitions.map(function (info) { return info.drillDefinition; }) }));
    }, []);
    var handlePushData = react_1.useCallback(function (data) {
        var _a;
        if ((_a = data.availableDrillTargets) === null || _a === void 0 ? void 0 : _a.attributes) {
            setPossibleDrills(function (prevValue) {
                // only set possible drills if really different to prevent other hooks firing unnecessarily
                if (!isEqual_1.default(prevValue, data.availableDrillTargets.attributes)) {
                    return data.availableDrillTargets.attributes;
                }
                // returning prevValue effectively skips the setState
                return prevValue;
            });
        }
    }, []);
    var chartConfig = react_1.useMemo(function () { return ({
        mapboxToken: dashboardViewConfig === null || dashboardViewConfig === void 0 ? void 0 : dashboardViewConfig.mapboxToken,
        separators: dashboardViewConfig === null || dashboardViewConfig === void 0 ? void 0 : dashboardViewConfig.separators,
        forceDisableDrillOnAxes: !drillableItems,
    }); }, [dashboardViewConfig, drillableItems]);
    // we need sdk-ui intl wrapper (this is how InsightView does this as well) for error messages etc.
    // ideally, we would merge InternalIntlWrapper and sdk-ui intl wrapper, but there is no clean way to do that
    var locale = (_b = dashboardViewConfig === null || dashboardViewConfig === void 0 ? void 0 : dashboardViewConfig.locale) !== null && _b !== void 0 ? _b : userWorkspaceSettings === null || userWorkspaceSettings === void 0 ? void 0 : userWorkspaceSettings.locale;
    /*
     * if there are drillable items from the user, use them and only them
     *
     * also pass any drillable items only if there is an onDrill specified, otherwise pass undefined
     * so that the items are not shown as active since nothing can happen on click without the onDrill provided
     */
    var drillableItemsToUse = onDrill ? drillableItems !== null && drillableItems !== void 0 ? drillableItems : implicitDrills : undefined;
    var insightPositionStyle = react_1.useMemo(function () {
        return {
            width: "100%",
            height: "100%",
            position: 
            // Headline violates the layout contract.
            // It should fit parent height and adapt to it as other visualizations.
            // Now, it works differently for the Headline - parent container adapts to Headline size.
            insight && sdk_model_1.insightVisualizationUrl(insight).includes("headline") ? "relative" : "absolute",
        };
    }, [insight]);
    return (react_1.default.createElement("div", { style: insightStyle },
        react_1.default.createElement("div", { style: insightPositionStyle },
            react_1.default.createElement(sdk_ui_1.IntlWrapper, { locale: locale },
                (status === "loading" || status === "pending" || isVisualizationLoading) && (react_1.default.createElement(LoadingComponent, null)),
                (error || visualizationError) && (react_1.default.createElement(InsightError_1.InsightError, { error: error || visualizationError, ErrorComponent: ErrorComponent, clientHeight: (userWorkspaceSettings === null || userWorkspaceSettings === void 0 ? void 0 : userWorkspaceSettings.enableKDWidgetCustomHeight) && clientHeight, height: null })),
                status === "success" && (react_1.default.createElement(InsightRenderer_1.InsightRenderer, { insight: insightWithAddedWidgetProperties, backend: effectiveBackend, workspace: effectiveWorkspace, drillableItems: drillableItemsToUse, onDrill: onDrill ? handleDrill : undefined, config: chartConfig, execConfig: execConfig, onLoadingChanged: handleLoadingChanged, locale: (_c = dashboardViewConfig.locale) !== null && _c !== void 0 ? _c : userWorkspaceSettings.locale, settings: userWorkspaceSettings, colorPalette: colorPalette, onError: handleError, pushData: handlePushData, ErrorComponent: ErrorComponent, LoadingComponent: LoadingComponent }))))));
};
//# sourceMappingURL=InsightRenderer.js.map